<template lang="html">
    <div class="hydrologic-variability-tables-mobile">
        <b-table
            v-if="distanceData"
            :data="formattedDistanceData"
            :is-fullwidth="true"
            :narrowed="true"
            :mobile-cards="false"
            class="candidates-table"
        >
            <template slot-scope="props">
                <b-table-column
                    field="month"
                    label="Month"
                >
                    {{ props.row.month }}
                </b-table-column>
                <b-table-column
                    field="candidate"
                    label="Candidate"
                    numeric
                >
                    {{ props.row.candidate }}
                </b-table-column>
            </template>
        </b-table>
        <b-table
            :data="formattedVariabilityData"
            :is-fullwidth="true"
            :narrowed="true"
            :mobile-cards="false"
            class="variability-table"
        >
            <template slot-scope="props">
                <b-table-column
                    field="month"
                    label="Month"
                >
                    {{ props.row.month }}
                </b-table-column>
                <b-table-column
                    field="variability"
                    label="Variability"
                    numeric
                >
                    <template
                        slot="header"
                        slot-scope="{ column }"
                    >
                        {{ column.label }}<sup>
                            <footnote-tooltip
                                :footnote-number="footnoteNumber"
                            />
                        </sup>
                    </template>
                    <!-- (90th - 10th) / 50th -->
                    {{ props.row.variability | numFormat('0,0.000') }}
                </b-table-column>
            </template>
        </b-table>

        <b-table
            :data="formatted10thData"
            :is-fullwidth="true"
            :narrowed="true"
            :mobile-cards="false"
            class="q10-table"
        >
            <template slot-scope="props">
                <b-table-column
                    field="month"
                    label="Month"
                >
                    {{ props.row.month }}
                </b-table-column>
                <b-table-column
                    field="data"
                    label="Q10 (m³/s) Low Flow"
                    numeric
                >
                    {{ props.row.data | numFormat('0,0.000') }}
                </b-table-column>
            </template>
        </b-table>
    </div>
</template>

<script>
import FootnoteTooltip from '@foundryspatial/footnote-tooltip';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export default {
    name: 'HydrologicVariabilityTableMobile',
    components: {
        FootnoteTooltip,
    },

    props: {
        data: {
            type: Object,
            required: true,
        },

        distanceData: {
            type: Array,
            required: false,
            default: null,
        },

        footnoteNumber: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        formatted10thData: [],
        formattedVariabilityData: [],
        formattedDistanceData: [],
        months: Array.from(Array(12)).map((e, i) => i + 1), // create array of values 1 - 12
    }),

    computed: {
        /**
         * dynamically select the first object of the data to be used for small screens
         */
        firstDataObject() {
            return Object.keys(this.data)[0];
        },
    },

    created() {
        this.format10thData();
        this.formatVariabilityData();
        if (this.distanceData) {
            this.formatDistanceData();
        }
    },

    methods: {
        /**
         * formats 10th percentile data into a simple object containing month name and data
         * the first object in the data is used so only a single data range is shown on small screens
         */
        format10thData() {
            Object.entries(this.data[this.firstDataObject]['10th']).forEach(([month, data]) => {
                this.formatted10thData.push(
                    {
                        month: this.monthNumberToName(month),
                        data,
                    },
                );
            });
        },

        /**
         * formats variability data into a simple object containing month name and data
         * the first object in the data is used so only a single data range is shown on small screens
         */
        formatVariabilityData() {
            this.months.forEach((month) => {
                // (90th - 10th) / 50th
                const variability = (this.data[this.firstDataObject]['90th'][month]
                    - this.data[this.firstDataObject]['10th'][month])
                    / this.data[this.firstDataObject]['50th'][month];
                this.formattedVariabilityData.push(
                    {
                        month: this.monthNumberToName(month),
                        variability,
                    },
                );
            });
        },

        /**
         * format distance data to find candidate with lowest value for each month
         */
        formatDistanceData() {
            // loop through each key for in the candidate object
            Object.keys(this.distanceData[0]).forEach((candidateKey) => {
                // only run reducer logic for months
                if (candidateKey !== 'candidate') {
                    const output = this.distanceData.reduce((acc, curr) => {
                        // return old values if new month value is null
                        if (curr[candidateKey] === null) return acc;

                        // if new value is smaller than old value, save new data to accumulator
                        if (acc.value > curr[candidateKey]) {
                            return {
                                month: this.monthNumberToName(candidateKey.slice(-2)),
                                candidate: curr.candidate,
                                value: curr[candidateKey],
                            };
                        }
                        return acc;
                    },
                    // initial value for starting comparison
                    { month: '', candidate: '', value: Infinity });

                    // save month, candidate, lowest value for every month
                    this.formattedDistanceData.push(output);
                }
            });
        },

        /**
         * Changes a month number into the relating month name eg. 1 = January
         * dayjs requires a year format so a static year and day are used with the dynamic month
         * @param {Number|String} month - month number that will be changed to it's word form
         * @param {String} format - optional parameter for passing in the date format
         * @returns {String} month name following the given format
         */
        monthNumberToName(month, format = 'MMMM') {
            return dayjs(`${month}`, 'M').format(format);
        },
    },
};
</script>

<style lang="scss">
.hydrologic-variability-tables-mobile {
    .b-table {
        margin-bottom: 2rem;

        .table-wrapper {
            overflow-x: hidden;
        }
    }
}

</style>
